import { ArrowForward, InfoOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAddressValidationFlow } from "components/AddressValidation";
import { InputBase, InputLabel } from "components/common/Input";
import { FormikContextType } from "formik";
import { useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Location } from "types/location";
import { getCountryNames } from "utils/countries";
import LocationContact from "./LocationContact";

type LocationFormFirstStepProps = {
  formik: FormikContextType<Location>;
  nextAsSubmit?: boolean;
  originalPath: string;
};

export default function LocationFormFirstStep({
  originalPath,
  formik,
  nextAsSubmit = false,
}: LocationFormFirstStepProps) {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const secondaryAddressLine = useRef<HTMLInputElement>(null);
  const { validate, ui: addressValidationUi } = useAddressValidationFlow({
    address: {
      address: formik.values.address,
      address2: formik.values.address2,
      city: formik.values.city,
      state: formik.values.state,
      zipcode: formik.values.zipcode,
      country: formik.values.country,
    },
    onAccepted: () => setSearchParams({ step: "2" }),
    onAddressChange: (address) => {
      formik.setFieldValue("address", address.address);
      formik.setFieldValue("address2", address.address2);
      formik.setFieldValue("city", address.city);
      formik.setFieldValue("state", address.state);
      formik.setFieldValue("zipcode", address.zipcode);
      formik.setFieldValue("country", address.country);
    },
    onMissingSubpremises: () => {
      secondaryAddressLine.current?.focus();
      formik.setFieldError(
        "address2",
        "The address may be missing a unit number. Please update and confirm the address."
      );
    },
  });

  return (
    <Stack gap={2}>
      <Box component={Paper} variant="outlined" sx={{ padding: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
          <ArrowForward />
          Address
        </Typography>
        <Grid container columns={2} spacing={2}>
          <Grid item xs={2}>
            <InputLabel htmlFor="location-address">Address</InputLabel>
            <TextField
              fullWidth
              required
              variant="outlined"
              size="small"
              id="location-address"
              {...formik.getFieldProps("address")}
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              id="location-address2"
              inputRef={secondaryAddressLine}
              error={!!formik.errors.address2}
              placeholder="Apt, suite, unit, building, floor, etc."
              {...formik.getFieldProps("address2")}
            />
          </Grid>
          <Grid item xs={1}>
            <InputLabel htmlFor="location-city">City/Region</InputLabel>
            <TextField
              fullWidth
              required
              variant="outlined"
              size="small"
              id="location-city"
              {...formik.getFieldProps("city")}
            />
          </Grid>
          <Grid item xs={1}>
            <InputLabel htmlFor="location-state">State/Province</InputLabel>
            <TextField
              fullWidth
              required
              variant="outlined"
              size="small"
              id="location-state"
              {...formik.getFieldProps("state")}
            />
          </Grid>
          <Grid item xs={1}>
            <InputLabel htmlFor="location-zipcode">Zip/Postal Code</InputLabel>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              id="location-zipcode"
              {...formik.getFieldProps("zipcode")}
            />
          </Grid>
          <Grid item xs={1}>
            <Stack direction="row" alignItems="start" gap={0.5}>
              <InputLabel htmlFor="location-country">Country</InputLabel>
              <Tooltip
                title={
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Revivn supports some international shipping
                    </Typography>
                    <Typography variant="inherit">
                      Our sales team will review and approve any pickup requests made outside of the US!
                    </Typography>
                  </>
                }
                placement="right"
              >
                <InfoOutlined color="primary" fontSize="small" />
              </Tooltip>
            </Stack>
            <Autocomplete
              id="location-country"
              options={getCountryNames()}
              value={formik.values.country || null}
              onChange={(_, value) => formik.setFieldValue("country", value ?? "")}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  size="small"
                  variant="outlined"
                  name="country"
                  error={formik.touched.country && !!formik.errors.country}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "country-name",
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={2}>
            <InputLabel htmlFor="location-name">Location Name</InputLabel>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              id="location-name"
              helperText="Give the location a nickname"
              {...formik.getFieldProps("name")}
            />
          </Grid>
          <Grid item xs={2}>
            <InputLabel htmlFor="users-count">No. of End Users</InputLabel>
            <TextField
              type="number"
              variant="outlined"
              size="small"
              id="users-count"
              helperText="Approximately how many end users does this location have?"
              {...formik.getFieldProps("approximatePeople")}
              value={formik.values.approximatePeople ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.preventDefault();
                const { value } = e.target;
                if (+value < 0) return;
                formik.setFieldValue("approximatePeople", value);
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <Box component={Paper} variant="outlined" sx={{ padding: 4 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <ArrowForward sx={{ mt: "4px", flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <FormControl variant="standard" sx={{ width: "100%" }}>
              <label htmlFor="access-instructions">
                <Typography variant="h6" fontWeight={"bold"}>
                  Access Instructions
                </Typography>
                <Typography>
                  Are there any instructions our crew needs to follow to access your building and the space to pickup
                  your assets?
                </Typography>
              </label>
              <InputBase multiline rows={3} id="access-instructions" {...formik.getFieldProps("accessInstructions")} />
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box component={Paper} variant="outlined" sx={{ padding: 4 }}>
        <LocationContact />
      </Box>

      <Box component={Paper} variant="outlined" sx={{ padding: 4 }}>
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button variant="outlined" onClick={() => navigate(originalPath)}>
            Back
          </Button>

          {nextAsSubmit ? (
            <Button variant="contained" type="submit">
              Submit
            </Button>
          ) : (
            <Button variant="contained" disabled={!formik.isValid} onClick={validate}>
              Next
            </Button>
          )}
        </Stack>
      </Box>

      {addressValidationUi}
    </Stack>
  );
}
